import moment from 'moment';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import type { Student } from '../../../api/types/student';
import type { SubscriptionDetails } from '../../../api/types/subscription';
import { openStudentHubWithSso } from '../../../util/openStudentHubWithSso';
import { useIsHubAuthenticated } from '../../app-shell/useIsHubAuthenticated';
import { userSignout } from '../../legacy/actions';
import api from '../../legacy/api';
import {
  PATH_HUB_ADD_STUDENT,
  PATH_HUB_MANAGE_SERVICE,
  PATH_HUB_SUBSCRIPTIONS,
  PATH_HUB_SUPPORT,
} from '../../legacy/pagePath';

// A subscription is valid if its paid-until date hasn't passed or is null (for bundled subscriptions)
const hasValidSubscription = (subscriptions: SubscriptionDetails[]) =>
  subscriptions.some(
    (subscription) =>
      subscription.subscriptionPaidUntilDate === null ||
      moment(subscription.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day')
  );

export type AccountHolder = {
  firstName: string;
  lastName: string;
  /** Role label shown beneath the account holder's name (e.g. "Admin"). */
  role: string;
};

// Container hook for the account switcher. Resolves the account holder and the
// list of students from hub's data layer and exposes the menu actions.
//
// Selecting a student with a valid subscription opens the Student Hub for
// them; selecting one without redirects to Manage Subscription instead —
// see `onSelectStudent`.
export const useAccountSwitcher = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isHubAuthenticated = useIsHubAuthenticated();
  const [selectingStudentId, setSelectingStudentId] = useState<string | null>(null);

  const { data: accountDetails, isLoading: isLoadingAccount } = api.account.details.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });
  const { data: students, isLoading: isLoadingStudents } = api.student.all.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = api.subscription.getAll.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });

  const accountHolder: AccountHolder | null = accountDetails
    ? { firstName: accountDetails.firstName, lastName: accountDetails.lastName, role: 'Admin' }
    : null;

  const signOut = useCallback(() => {
    dispatch(userSignout());
  }, [dispatch]);

  const onSelectStudent = useCallback(
    async (student: Student) => {
      if (selectingStudentId) {
        return;
      }

      const studentSubscriptions = subscriptionsData?.find((sub) => sub.studentId === student.studentId);
      if (hasValidSubscription(studentSubscriptions?.subscriptions ?? [])) {
        setSelectingStudentId(student.studentId);
        const opened = await openStudentHubWithSso({
          target: '_self',
          returnTo: `/?student=${student.customerNumber}`,
        });
        // Keep the spinner while `_self` navigation proceeds; clear only on failure.
        if (!opened) {
          setSelectingStudentId(null);
        }
        return;
      }
      history.push(PATH_HUB_SUBSCRIPTIONS, { studentId: student.studentId });
    },
    [subscriptionsData, history, selectingStudentId]
  );

  const onAddStudent = useCallback(() => {
    history.push(PATH_HUB_ADD_STUDENT);
  }, [history]);

  const onSupport = useCallback(() => {
    history.push(PATH_HUB_SUPPORT);
  }, [history]);

  return {
    accountHolder,
    students: students ?? [],
    // A disabled react-query stays in status "loading" forever (v4 doesn't
    // distinguish "disabled" from "pending"), so gate on auth too, or an
    // anonymous viewer would see the loading skeleton indefinitely instead of
    // the component resolving to null.
    isLoading: isHubAuthenticated && (isLoadingAccount || isLoadingStudents || isLoadingSubscriptions),
    selectingStudentId,
    signOut,
    onSelectStudent,
    onAddStudent,
    onSupport,
    isAddStudentActive: Boolean(useRouteMatch({ path: PATH_HUB_ADD_STUDENT, exact: false })),
    isSupportActive: Boolean(useRouteMatch({ path: PATH_HUB_MANAGE_SERVICE, exact: false })),
  };
};
